import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link, Outlet, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CommandPalette, type CommandItem } from '@teals/ui-web';
import { useDispatch } from 'react-redux';
import { useThemeToggle } from '../hooks/useThemeToggle';
import { useAuth } from '../hooks/useAuth';
import { useCommandPalette } from '../hooks/useCommandPalette';
import RequireOrg from '../components/RequireOrg';
import { useIsStaff, useIsSuperUser } from '../components/RequireOperator';
import { useOrgBillingAccess } from '../components/RequireOrgBilling';
import { setActiveOrganization } from '../store/slices';
import {
  useGetOrganisationQuery,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
} from '../store/api';

const NAV_ITEMS = [
  { to: '/agents', label: 'Agents' },
  { to: '/applications', label: 'Applications' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/analytics', label: 'Analytics' },
];

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 1.5rem;
  max-width: 1440px;
  margin: 0 auto;
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  min-width: 0;
`;

const NavBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--text-primary);
  cursor: pointer;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledNavLink = styled(NavLink)`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
    text-decoration: none;
  }

  &.active {
    background: var(--accent-subtle);
    color: var(--accent);
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const CmdTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-muted);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 220px;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--text-secondary);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Kbd = styled.kbd`
  font-family: inherit;
  font-size: 0.6875rem;
  padding: 2px 6px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  margin-left: auto;
  color: var(--text-muted);
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`;

const UserDropdown = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 220px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  z-index: 200;
  overflow: hidden;
`;

const dropdownItemCss = `
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  text-decoration: none;
  box-sizing: border-box;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
    text-decoration: none;
  }
`;

const DropdownItem = styled.button`
  ${dropdownItemCss}
`;

/** Real anchors so the browser offers Open in new tab / window. */
const DropdownLink = styled(Link)`
  ${dropdownItemCss}
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--border);
`;

const DropdownHeader = styled.div`
  padding: 0.75rem 1rem 0.625rem;
`;

const DropdownName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const DropdownEmail = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const PageBody = styled.main`
  flex: 1;
  padding-bottom: 64px;

  @media (min-width: 769px) {
    padding-bottom: 0;
  }
`;

const MobileNav = styled.nav`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: var(--nav-bg);
  border-top: 1px solid var(--nav-border);
  z-index: 100;
  padding: 0 0.5rem;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
`;

const MobileNavItem = styled(NavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0.5rem;
  border-radius: 8px;
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s;

  &.active {
    color: var(--accent);
  }

  &:hover {
    color: var(--text-primary);
    text-decoration: none;
  }
`;

const MobileNavIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

const OrgSwitcher = styled.select`
  max-width: 180px;
  height: 36px;
  padding: 0 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.8125rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const AppLayout: React.FC = () => {
  const { mode, toggle, set: setMode } = useThemeToggle();
  const { user, logout } = useAuth();
  const isStaff = useIsStaff();
  const isSuperUser = useIsSuperUser();
  const { allowed: canAccessBilling } = useOrgBillingAccess();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, open, close } = useCommandPalette();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const orgId = user?.organizationId;
  const { data: organisation } = useGetOrganisationQuery(orgId!, {
    skip: !orgId,
  });
  const { data: preferences } = useGetPreferencesQuery(undefined, {
    skip: !user,
  });
  const [updatePreferences] = useUpdatePreferencesMutation();

  useEffect(() => {
    if (preferences?.theme && preferences.theme !== mode) {
      setMode(preferences.theme);
    }
    // Only hydrate from server when preferences first load / change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit mode to avoid fighting local toggles
  }, [preferences?.theme, setMode]);

  const orgOptions = useMemo(() => {
    if (!orgId) return [];
    return [
      {
        id: orgId,
        name: organisation?.name || orgId,
      },
    ];
  }, [orgId, organisation?.name]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const paletteItems: CommandItem[] = useMemo(
    () => [
      ...NAV_ITEMS.map((item) => ({
        id: item.to,
        label: item.label,
        description: `Go to ${item.label}`,
        group: 'Navigation',
      })),
      {
        id: '/integrations?category=data_source',
        label: 'Data sources',
        description: 'Integrations filtered to data sources',
        group: 'Navigation',
      },
      {
        id: '/business',
        label: 'Business Center',
        description: 'Organisation management',
        group: 'Navigation',
      },
      {
        id: '/profile',
        label: 'Profile',
        description: 'Manage your profile',
        group: 'Navigation',
      },
      ...(canAccessBilling
        ? [
            {
              id: '/payments',
              label: 'Payments',
              description: 'Billing and invoices',
              group: 'Navigation',
            },
          ]
        : []),
      {
        id: '/settings',
        label: 'Settings',
        description: 'Appearance and notification preferences',
        group: 'Navigation',
      },
      {
        id: '/mcp',
        label: 'Lumina MCP',
        description: 'Cursor tokens and audit',
        group: 'Navigation',
      },
      ...(isStaff
        ? [
            {
              id: '/staff',
              label: 'Staff',
              description: 'Platform operator console',
              group: 'Navigation',
            },
            {
              id: '/staff?tab=pricing',
              label: 'Staff · Pricing',
              description: 'Commercial defaults and org overrides',
              group: 'Navigation',
            },
          ]
        : []),
      ...(isSuperUser
        ? [
            {
              id: '/super',
              label: 'Super User',
              description: 'Irreversible platform actions',
              group: 'Navigation',
            },
          ]
        : []),
      {
        id: '/agents/new',
        label: 'Create agent',
        description: 'Describe a new agent with Agent Builder',
        group: 'Actions',
      },
      {
        id: 'action:toggle-theme',
        label: mode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode',
        description: 'Toggle light/dark theme',
        group: 'Actions',
      },
      {
        id: 'action:logout',
        label: 'Sign Out',
        description: 'Log out of your account',
        group: 'Actions',
      },
    ],
    [mode, isStaff, isSuperUser, canAccessBilling],
  );

  const handleSelect = (item: CommandItem) => {
    if (item.id === 'action:toggle-theme') {
      const next = mode === 'light' ? 'dark' : 'light';
      toggle();
      void updatePreferences({ theme: next });
    } else if (item.id === 'action:logout') {
      handleLogout();
    } else {
      navigate(item.id);
    }
    close();
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <>
      <Nav>
        <NavInner>
          <NavLeft>
            <NavBrand onClick={() => navigate('/agents')}>
              <LogoIcon>
                <svg viewBox="0 0 32 32" width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="7" fill="var(--accent)" />
                  <path d="M16 4Q16 16 4 16Q16 16 16 28Q16 16 28 16Q16 16 16 4Z" fill="white" />
                  <circle cx="24" cy="7" r="1.8" fill="white" opacity="0.6" />
                </svg>
              </LogoIcon>
              Luminafactory
            </NavBrand>

            <NavLinks>
              {NAV_ITEMS.map((item) => (
                <StyledNavLink key={item.to} to={item.to}>
                  {item.label}
                </StyledNavLink>
              ))}
            </NavLinks>
          </NavLeft>

          <NavActions>
            {orgOptions.length > 0 && (
              <OrgSwitcher
                aria-label="Active organisation"
                value={orgId}
                onChange={(e) =>
                  dispatch(setActiveOrganization(e.target.value))
                }
                title="Active organisation (sent as x-organization-id)"
              >
                {orgOptions.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </OrgSwitcher>
            )}
            <CmdTrigger onClick={open}>
              Search or ask...
              <Kbd>&#x2318;K</Kbd>
            </CmdTrigger>
            <UserMenuWrapper ref={menuRef}>
              <UserAvatar
                role="button"
                tabIndex={0}
                onClick={() => setMenuOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMenuOpen((prev) => !prev);
                  }
                }}
                title="Account menu"
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                {user?.name
                  ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                  : 'U'}
              </UserAvatar>
              <UserDropdown $open={menuOpen} role="menu" aria-label="Account settings">
                <DropdownHeader>
                  <DropdownName>{user?.name || 'Account'}</DropdownName>
                  {user?.email ? <DropdownEmail>{user.email}</DropdownEmail> : null}
                </DropdownHeader>
                <DropdownDivider />
                <DropdownLink
                  role="menuitem"
                  to="/business"
                  onClick={() => setMenuOpen(false)}
                >
                  Business Center
                </DropdownLink>
                <DropdownLink
                  role="menuitem"
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                >
                  Profile
                </DropdownLink>
                {canAccessBilling && (
                  <DropdownLink
                    role="menuitem"
                    to="/payments"
                    onClick={() => setMenuOpen(false)}
                  >
                    Payments
                  </DropdownLink>
                )}
                <DropdownLink
                  role="menuitem"
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                >
                  Settings
                </DropdownLink>
                <DropdownLink
                  role="menuitem"
                  to="/mcp"
                  onClick={() => setMenuOpen(false)}
                >
                  Lumina MCP
                </DropdownLink>
                {isStaff && (
                  <DropdownLink
                    role="menuitem"
                    to="/staff"
                    onClick={() => setMenuOpen(false)}
                  >
                    Staff
                  </DropdownLink>
                )}
                {isSuperUser && (
                  <DropdownLink
                    role="menuitem"
                    to="/super"
                    onClick={() => setMenuOpen(false)}
                  >
                    Super User
                  </DropdownLink>
                )}
                <DropdownDivider />
                <DropdownItem role="menuitem" onClick={handleLogout}>
                  Log Out
                </DropdownItem>
              </UserDropdown>
            </UserMenuWrapper>
          </NavActions>
        </NavInner>
      </Nav>

      <PageBody>
        <RequireOrg>
          <Outlet />
        </RequireOrg>
      </PageBody>

      <MobileNav>
        <MobileNavItem to="/agents">
          <MobileNavIcon>&#x1F916;</MobileNavIcon>
          Agents
        </MobileNavItem>
        <MobileNavItem to="/applications">
          <MobileNavIcon>&#x1F465;</MobileNavIcon>
          Apps
        </MobileNavItem>
        <MobileNavItem to="/integrations">
          <MobileNavIcon>&#x1F517;</MobileNavIcon>
          Integrations
        </MobileNavItem>
        <MobileNavItem to="/analytics">
          <MobileNavIcon>&#x1F4CA;</MobileNavIcon>
          Analytics
        </MobileNavItem>
      </MobileNav>

      <CommandPalette
        isOpen={isOpen}
        onClose={close}
        onSelect={handleSelect}
        items={paletteItems}
        placeholder="Search pages or run actions..."
      />
    </>
  );
};

export default AppLayout;
